import React, { useState, useEffect } from 'react';
import { X, Search, Save, RotateCcw, Package, Calendar, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import '../styles/PODUpdateForm.css';
import apiClient from '../services/api';

const PODUpdateForm = ({ onClose, show }) => {
  const navigate = useNavigate();  // ✅ For route-based usage
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Search state
  const [awbSearch, setAwbSearch] = useState('');
  const [consignmentData, setConsignmentData] = useState(null);
  const [showSelectionList, setShowSelectionList] = useState(false);
  const [consignmentList, setConsignmentList] = useState([]);
  
  // POD Update state
  const [podData, setPodData] = useState({
    DeliveryDate: new Date().toISOString().slice(0, 10),
    DeliveredBy: '',
    ReceivedBy: '',
    CourierStatusID: '',
    DeliveredByID: '',
    PODRemarks: '',
    OtherChargeDescription: ''
  });
  
  // Dropdown data
  const [courierStatuses, setCourierStatuses] = useState([]);
  const [employees, setEmployees] = useState([]);
  
  // Session
  const session = getSessionData();
  
  function getSessionData() {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session:', error);
      return null;
    }
  }
  
  useEffect(() => {
    loadDropdownData();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showSelectionList) {
          setShowSelectionList(false);
        } else {
          handleClose();
        }
      }
      // F5 for Save
      if (e.key === 'F5') {
        e.preventDefault();
        if (consignmentData) handleSave();
      }
      // F6 for Search
      if (e.key === 'F6') {
        e.preventDefault();
        handleSearch();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSelectionList, consignmentData]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── DROPDOWN LOADING ────────────────────────────────────────────
  const loadDropdownData = async () => {
    console.log('Loading dropdown data...');
    const companyId = session?.companyid;

    try {
      // ✅ Load Courier Statuses
      console.log('Fetching courier statuses...');
      const statusResponse = await apiClient.get('/CourierStatus.php');
      console.log('Courier Status Response:', statusResponse.data);
      
      if (statusResponse.data?.success && Array.isArray(statusResponse.data.data)) {
        setCourierStatuses(statusResponse.data.data);
        console.log('✓ Courier statuses loaded:', statusResponse.data.data.length);
      } else {
        console.warn('Courier statuses response invalid:', statusResponse.data);
      }
    } catch (err) {
      console.error('❌ CourierStatus load failed:', err.response?.status, err.response?.data || err.message);
    }

    try {
      // ✅ Load Employees
      console.log('Fetching employees with companyId:', companyId);
      const empResponse = await apiClient.get('/Employees.php', {
        params: { 
          companyid: companyId,
          isactive: 1 
        }
      });
      console.log('Employees Response:', empResponse.data);
      
      if (empResponse.data?.success) {
        const empData = empResponse.data.data || [];
        setEmployees(Array.isArray(empData) ? empData : []);
        console.log('✓ Employees loaded:', empData.length, 'employees');
        if (empData.length > 0) {
          console.log('First employee sample:', empData[0]);
        }
      } else {
        console.warn('Employees response invalid:', empResponse.data);
        setEmployees([]);
      }
    } catch (err) {
      console.error('❌ Employees load failed:', err.response?.status, err.response?.data || err.message);
      setEmployees([]);
    }
  };

  // ─── SEARCH ──────────────────────────────────────────────────────
  const handleSearch = async () => {
    if (!awbSearch.trim()) {
      setError('Please enter AWB/Consignment number');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const searchResponse = await apiClient.get(`/ConsignmentPOD.php?search=${awbSearch}`);
      if (searchResponse.data.success && searchResponse.data.count > 0) {
        const results = searchResponse.data.data;
        if (results.length === 1) {
          loadConsignmentDetails(results[0].ConsignmentNo);
        } else {
          setConsignmentList(results);
          setShowSelectionList(true);
          setError('');
        }
      } else {
        setError('No consignments found matching "' + awbSearch + '"');
        setConsignmentData(null);
      }
    } catch (err) {
      console.error('Error searching:', err);
      setError('Error searching: ' + (err.response?.data?.message || err.message));
      setConsignmentData(null);
    } finally {
      setLoading(false);
    }
  };

  const loadConsignmentDetails = async (consignmentNo) => {
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get(`/ConsignmentPOD.php?awb=${consignmentNo}`);
      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setConsignmentData(data);
        setPodData({
          DeliveryDate: data.DeliveryDate || new Date().toISOString().slice(0, 10),
          DeliveredBy: data.DeliveredBy || '',
          ReceivedBy: data.ReceivedBy || '',
          CourierStatusID: data.CourierStatusID || '',
          DeliveredByID: data.DeliveredByID || '',
          PODRemarks: data.PODRemarks || '',
          OtherChargeDescription: data.OtherChargeDescription || ''
        });
        setShowSelectionList(false);
        setError('');
      } else {
        setError('Consignment not found');
        setConsignmentData(null);
      }
    } catch (err) {
      console.error('Error loading details:', err);
      setError('Error loading: ' + (err.response?.data?.message || err.message));
      setConsignmentData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectConsignment = (consignment) => {
    setAwbSearch(consignment.ConsignmentNo);
    loadConsignmentDetails(consignment.ConsignmentNo);
  };

  // ─── FORM HANDLERS ───────────────────────────────────────────────
  const handlePODChange = (e) => {
    const { name, value } = e.target;
    setPodData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSave = async () => {
    if (!consignmentData) { setError('No consignment loaded'); return; }
    if (!podData.DeliveryDate) { setError('Delivery date is required'); return; }
    setLoading(true);
    setError('');
    try {
      const dataToSave = { ConsignmentID: consignmentData.ConsignmentID, ...podData };
      const response = await apiClient.post('/ConsignmentPOD.php', dataToSave);
      if (response.data.success) {
        alert('POD updated successfully!');
        handleClear();
      } else {
        setError(response.data.message || 'Failed to save');
      }
    } catch (err) {
      console.error('Error saving:', err);
      setError('Error saving POD: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };
  
  const handleClear = () => {
    setAwbSearch('');
    setConsignmentData(null);
    setPodData({
      DeliveryDate: new Date().toISOString().slice(0, 10),
      DeliveredBy: '',
      ReceivedBy: '',
      CourierStatusID: '',
      DeliveredByID: '',
      PODRemarks: '',
      OtherChargeDescription: ''
    });
    setError('');
    setShowSelectionList(false);
    setConsignmentList([]);
  };

  // ✅ FIXED: Smart close handler that works for both modal and route
  const handleClose = () => {
    console.log('=== CLOSE HANDLER ===');
    console.log('onClose type:', typeof onClose);
    console.log('onClose value:', onClose);
    
    // Method 1: If onClose function provided (modal usage)
    if (typeof onClose === 'function') {
      console.log('✓ Using onClose prop (modal mode)');
      onClose();
      return;
    }
    
    // Method 2: If used as a route, navigate back
    console.log('✓ Using navigate (route mode)');
    try {
      navigate('/dashboard');  // Go back to dashboard
      console.log('Navigated to dashboard');
    } catch (error) {
      console.error('Navigate failed:', error);
      // Fallback: try history.back()
      window.history.back();
    }
  };

  // ─── SESSION GUARD ───────────────────────────────────────────────
  if (!session?.companyid) {
    return (
      <div className="pod-form-overlay" onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
        <div className="pod-form-container" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
          <div className="form-header">
            <div className="header-left">
              <Package size={24} />
              <h2>Session Required</h2>
            </div>
            <button 
              className="close-btn" 
              type="button" 
              onClick={handleClose} 
              title="Close (ESC)"
            >
              <X size={20} />
            </button>
          </div>
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <p>Please select a company and financial year from the dashboard.</p>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if show is false
  if (show === false) {
    return null;
  }

  // ─── MAIN RENDER ─────────────────────────────────────────────────
  return (
    <div className="pod-form-overlay" onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
      <div className="pod-form-container" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="form-header">
          <div className="header-left">
            <Package size={24} />
            <h2>POD Update</h2>
          </div>
          <button 
            className="close-btn" 
            type="button" 
            onClick={handleClose} 
            title="Close (ESC)"
            style={{ cursor: 'pointer' }}
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Toolbar */}
        <div className="pod-toolbar">
          <button type="button" onClick={handleSearch} disabled={loading} className="btn-toolbar">
            <Search size={18} /> Search (F6)
          </button>
          <button type="button" onClick={handleSave} disabled={loading || !consignmentData} className="btn-toolbar btn-save">
            <Save size={18} /> Save (F5)
          </button>
          <button type="button" onClick={handleClear} disabled={loading} className="btn-toolbar">
            <RotateCcw size={18} /> Clear (Esc)
          </button>
        </div>
        
        {error && (
          <div className="alert alert-error">
            <strong>Error:</strong> {error}
          </div>
        )}
        
        {/* Search Section */}
        <div className="pod-search-section">
          <label>AWB / Consignment No:</label>
          <input
            type="text"
            value={awbSearch}
            onChange={(e) => setAwbSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Enter consignment number"
            disabled={loading}
            autoFocus
          />
          <button type="button" onClick={handleSearch} disabled={loading} className="btn-search-inline">
            <Search size={18} />
          </button>
        </div>
        
        <div className="form-content">
          {/* ── Selection Modal ── */}
          {showSelectionList && (
            <div className="selection-modal" onClick={() => setShowSelectionList(false)}>
              <div className="selection-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="selection-modal-header">
                  <h3>
                    <Package size={20} />
                    Select Consignment ({consignmentList.length} found)
                  </h3>
                  <button type="button" onClick={() => setShowSelectionList(false)} className="btn-close-modal">
                    <X size={20} />
                  </button>
                </div>
                <div className="selection-list">
                  <table className="selection-table">
                    <thead>
                      <tr>
                        <th>Consignment No</th>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Shipper</th>
                        <th>Consignee</th>
                        <th>Pcs</th>
                        <th>Weight</th>
                        <th>Status</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {consignmentList.map((cons) => (
                        <tr key={cons.ConsignmentID}>
                          <td className="cons-no">{cons.ConsignmentNo}</td>
                          <td>{new Date(cons.ConsignmentDate).toLocaleDateString()}</td>
                          <td><span className="type-badge">{cons.ConsignmentType || 'LT'}</span></td>
                          <td>{cons.ShipperName || '-'}</td>
                          <td>{cons.ConsigneeName || '-'}</td>
                          <td className="text-right">{cons.Pieces || 0}</td>
                          <td className="text-right">{cons.Weight || 0} kg</td>
                          <td>{cons.Status || '-'}</td>
                          <td>
                            <button type="button" onClick={() => handleSelectConsignment(cons)} className="btn-select">
                              <CheckCircle size={16} /> Select
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
  
          {/* ── Consignment Details ── */}
          {consignmentData && (
            <div className="form-section">
              <h3 className="section-title">
                <Package size={18} /> Consignment Details
              </h3>
              <div className="details-grid">
                <div className="detail-item">
                  <label>Consignment No:</label>
                  <div className="detail-value">{consignmentData.ConsignmentNo}</div>
                </div>
                <div className="detail-item">
                  <label>Date:</label>
                  <div className="detail-value">{new Date(consignmentData.ConsignmentDate).toLocaleDateString()}</div>
                </div>
                <div className="detail-item">
                  <label>Shipper:</label>
                  <div className="detail-value">{consignmentData.ShipperName}</div>
                </div>
                <div className="detail-item">
                  <label>Consignee:</label>
                  <div className="detail-value">{consignmentData.ConsigneeName}</div>
                </div>
                <div className="detail-item">
                  <label>Origin:</label>
                  <div className="detail-value">{consignmentData.OriginCountry}</div>
                </div>
                <div className="detail-item">
                  <label>Destination:</label>
                  <div className="detail-value">{consignmentData.DestinationCountry}</div>
                </div>
                <div className="detail-item">
                  <label>Pieces:</label>
                  <div className="detail-value">{consignmentData.Pieces}</div>
                </div>
                <div className="detail-item">
                  <label>Weight:</label>
                  <div className="detail-value">{consignmentData.StatedWeight} kg</div>
                </div>
                <div className="detail-item full-width">
                  <label>Description:</label>
                  <div className="detail-value">{consignmentData.Description}</div>
                </div>
              </div>
            </div>
          )}
          
          {/* ── POD Update ── */}
          {consignmentData && (
            <div className="form-section">
              <h3 className="section-title">
                <Calendar size={18} /> POD Update
              </h3>
              <div className="form-grid">

                {/* Delivery Date */}
                <div className="form-group">
                  <label>Delivery Date *</label>
                  <input type="date" name="DeliveryDate" value={podData.DeliveryDate} onChange={handlePODChange} required />
                </div>

                {/* ✅ FIXED: Delivered By Employee Dropdown */}
                <div className="form-group">
                  <label>Delivered By (Employee)</label>
                  <select 
                    name="DeliveredByID" 
                    value={podData.DeliveredByID} 
                    onChange={handlePODChange}
                  >
                    <option value="">-- Select Employee --</option>
                    {employees.length > 0 ? (
                      employees.map(emp => (
                        <option key={emp.employeeid} value={emp.employeeid}>
                          {emp.employeename}{emp.employeecode ? ` (${emp.employeecode})` : ''}
                        </option>
                      ))
                    ) : (
                      <option value="" disabled>No employees found</option>
                    )}
                  </select>
                  {employees.length === 0 && (
                    <small style={{ color: '#ef4444', fontSize: '12px' }}>
                      No active employees. Check Employees master.
                    </small>
                  )}
                </div>

                {/* Delivered By (Name) */}
                <div className="form-group">
                  <label>Delivered By (Name)</label>
                  <input type="text" name="DeliveredBy" value={podData.DeliveredBy} onChange={handlePODChange} placeholder="Person who delivered" />
                </div>

                {/* Received By */}
                <div className="form-group">
                  <label>Received By</label>
                  <input type="text" name="ReceivedBy" value={podData.ReceivedBy} onChange={handlePODChange} placeholder="Person who received" />
                </div>

                {/* Courier Status */}
                <div className="form-group">
                  <label>Courier Status</label>
                  <select name="CourierStatusID" value={podData.CourierStatusID} onChange={handlePODChange}>
                    <option value="">-- Select Status --</option>
                    {courierStatuses.map(status => (
                      <option key={status.CourierStatusID} value={status.CourierStatusID}>
                        {status.StatusName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* POD Remarks */}
                <div className="form-group full-width">
                  <label>POD Remarks</label>
                  <textarea name="PODRemarks" value={podData.PODRemarks} onChange={handlePODChange} rows="3" placeholder="Any remarks regarding delivery..." />
                </div>

                {/* Other Charge Description */}
                <div className="form-group full-width">
                  <label>Other Charge Description</label>
                  <input type="text" name="OtherChargeDescription" value={podData.OtherChargeDescription} onChange={handlePODChange} placeholder="Additional charges description" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PODUpdateForm;